import { ModalBareboneProps } from '@zeiss/beyond-online-react';
import { GeneralModal } from '@zeiss/lib-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { showToast } from 'src/helpers/showToast';
import { useUpdateTenantUsers } from 'src/hooks/api';
import { UserSearch } from 'src/modules/TenantManagement/components/UserSearch';
import { StandardRouteParams } from 'src/types';
import { SearchUsersResponse } from 'src/types/rest/managementPortalApi';

type AddMemberModalProps = Pick<ModalBareboneProps, 'isOpen' | 'onClose'>;

export const AddMemberModal = ({ isOpen, onClose }: AddMemberModalProps) => {
  const { t } = useTranslation(['common']);
  const { tenantId } = useParams<StandardRouteParams>();

  const [members, setMembers] = React.useState<SearchUsersResponse[]>([]);

  const { isPending, mutate: updateTenantUsers } = useUpdateTenantUsers(
    tenantId!,
  );

  const closeModal = () => {
    setMembers([]);
    onClose?.();
  };

  const addMembers = () => {
    updateTenantUsers(
      {
        idsToAdd: members.map(({ id }) => id),
      },
      {
        onError: () => {
          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.added'),
                x: t('common:glossary.member', { count: members.length }),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: () => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.updated'),
                x: t('common:glossary.tenant'),
              }),
              type: 'success',
            },
          });

          closeModal();
        },
      },
    );
  };

  const count = members.length ? members.length : 1;

  return (
    <GeneralModal
      acceptButtonText={t('common:action.addX', {
        x: t('common:glossary.member', { count }),
      })}
      disabled={!members.length}
      headline={t('common:action.addX', {
        x: t('common:glossary.member', { count }),
      })}
      isOpen={isOpen}
      loading={isPending}
      onAccept={addMembers}
      onClose={closeModal}
      size="m"
    >
      {/*
        NOTE: There is a technical limitation here, as we cannot filter the `UserSearch`
        with existing members of the tenant since that data is paginated and fetching
        all the existing members ahead of time is not feasible. This means that members
        are only filtered out in a given 'add member(s)' session, however, if the user
        searches for existing members, they will appear in the search results. This
        does not have any impact on saving the new members because the backend only
        adds the users that are not already members.
      */}
      <UserSearch
        colSpan={[4, 6, 12]}
        handleUserSelect={setMembers}
        id="applicationListManagers"
        label={t('common:glossary.member')}
        restrictToZeissEmailDomain={false}
        selectedUsers={members}
        /**
         * Need to increase zIndex of Autocomplete List if used in a Modal.
         * Modal backdrop zIndex is 50 (--z-index-overlay), so we need at least zIndex 51 here.
         */
        zIndexList={51}
      />
    </GeneralModal>
  );
};
